/**
 * Shared procedure type constants used across janus and portus apps.
 * This prevents code duplication and ensures consistency.
 */

export enum ProcedureTypes {
  VEHICLE_NOTIFICATION = "VEHICLE_NOTIFICATION",
  VEHICLE_REGISTRATION = "VEHICLE_REGISTRATION",
  VEHICLE_TRANSFER = "VEHICLE_TRANSFER",
  VEHICLE_SIMULTANEOUS = "VEHICLE_SIMULTANEOUS",
  VEHICLE_DEREGISTRATION = "VEHICLE_DEREGISTRATION",
  VEHICLE_DUPLICATE = "VEHICLE_DUPLICATE",
  VEHICLE_REACTIVATION = "VEHICLE_REACTIVATION",
  VEHICLE_LEASING_RELEASE = "VEHICLE_LEASING_RELEASE",
  VEHICLE_RED_PLATE = "VEHICLE_RED_PLATE",
  VEHICLE_GREEN_PLATE = "VEHICLE_GREEN_PLATE",
  VEHICLE_REFORM_ANNOTATION = "VEHICLE_REFORM_ANNOTATION",
  VEHICLE_TRANSFER_RECEIPT = "VEHICLE_TRANSFER_RECEIPT",
  VEHICLE_SERVICE_CHANGE = "VEHICLE_SERVICE_CHANGE",
  VEHICLE_ADDRESS_CHANGE = "VEHICLE_ADDRESS_CHANGE",
  VEHICLE_ENV_BADGE = "VEHICLE_ENV_BADGE",
  TAX_REDUCED_VAT = "TAX_REDUCED_VAT",
  TAX_IEDMT = "TAX_IEDMT",
  TAX_IVTM = "TAX_IVTM",
  TAX_ITP = "TAX_ITP",
  VEHICLE_TRANSPORT_CARD = "VEHICLE_TRANSPORT_CARD",
  PROPERTY_NOTE = "PROPERTY_NOTE",
  DOCUMENT_WRITING = "DOCUMENT_WRITING",
  PLAN_AUTO_PLUS = "PLAN_AUTO_PLUS",
}

/**
 * Ordered list of procedure types for dropdown options.
 */
export const procedureTypeOptions: ProcedureTypes[] = [
  ProcedureTypes.VEHICLE_REGISTRATION,
  ProcedureTypes.VEHICLE_NOTIFICATION,
  ProcedureTypes.VEHICLE_TRANSFER,
  ProcedureTypes.VEHICLE_SIMULTANEOUS,
  ProcedureTypes.VEHICLE_DEREGISTRATION,
  ProcedureTypes.VEHICLE_DUPLICATE,
  ProcedureTypes.VEHICLE_REACTIVATION,
  ProcedureTypes.VEHICLE_LEASING_RELEASE,
  ProcedureTypes.VEHICLE_RED_PLATE,
  ProcedureTypes.VEHICLE_GREEN_PLATE,
  ProcedureTypes.VEHICLE_REFORM_ANNOTATION,
  ProcedureTypes.VEHICLE_TRANSFER_RECEIPT,
  ProcedureTypes.VEHICLE_SERVICE_CHANGE,
  ProcedureTypes.VEHICLE_ADDRESS_CHANGE,
  ProcedureTypes.VEHICLE_ENV_BADGE,
  ProcedureTypes.TAX_REDUCED_VAT,
  ProcedureTypes.TAX_IEDMT,
  ProcedureTypes.TAX_IVTM,
  ProcedureTypes.TAX_ITP,
  ProcedureTypes.VEHICLE_TRANSPORT_CARD,
  ProcedureTypes.PROPERTY_NOTE,
  ProcedureTypes.DOCUMENT_WRITING,
  ProcedureTypes.PLAN_AUTO_PLUS,
];

/**
 * Procedure types pinned to the top of any type-selection UI (creation
 * form, list filter). Single source of truth for the "priority order" —
 * keep both Janus and Portus surfaces consistent.
 */
export const pinnedProcedureTypes: ProcedureTypes[] = [
  ProcedureTypes.PLAN_AUTO_PLUS,
  ProcedureTypes.VEHICLE_REGISTRATION,
  ProcedureTypes.VEHICLE_TRANSFER,
  ProcedureTypes.VEHICLE_NOTIFICATION,
  ProcedureTypes.VEHICLE_SIMULTANEOUS,
  ProcedureTypes.VEHICLE_DEREGISTRATION,
];

/**
 * Spanish grammatical gender for each procedure type.
 * Used for proper article agreement (el/la, un/una).
 */
export const procedureTypeGender: Record<ProcedureTypes, "male" | "female"> = {
  [ProcedureTypes.VEHICLE_NOTIFICATION]: "female",
  [ProcedureTypes.VEHICLE_REGISTRATION]: "female",
  [ProcedureTypes.VEHICLE_TRANSFER]: "female",
  [ProcedureTypes.VEHICLE_SIMULTANEOUS]: "female",
  [ProcedureTypes.VEHICLE_DEREGISTRATION]: "female",
  [ProcedureTypes.VEHICLE_DUPLICATE]: "male",
  [ProcedureTypes.VEHICLE_REACTIVATION]: "female",
  [ProcedureTypes.VEHICLE_LEASING_RELEASE]: "female",
  [ProcedureTypes.VEHICLE_RED_PLATE]: "female",
  [ProcedureTypes.VEHICLE_GREEN_PLATE]: "female",
  [ProcedureTypes.VEHICLE_REFORM_ANNOTATION]: "female",
  [ProcedureTypes.VEHICLE_TRANSFER_RECEIPT]: "male",
  [ProcedureTypes.VEHICLE_SERVICE_CHANGE]: "male",
  [ProcedureTypes.VEHICLE_ADDRESS_CHANGE]: "male",
  [ProcedureTypes.VEHICLE_ENV_BADGE]: "male",
  [ProcedureTypes.TAX_REDUCED_VAT]: "male",
  [ProcedureTypes.TAX_IEDMT]: "male",
  [ProcedureTypes.TAX_IVTM]: "male",
  [ProcedureTypes.TAX_ITP]: "male",
  [ProcedureTypes.VEHICLE_TRANSPORT_CARD]: "female",
  [ProcedureTypes.PROPERTY_NOTE]: "female",
  [ProcedureTypes.DOCUMENT_WRITING]: "male",
  [ProcedureTypes.PLAN_AUTO_PLUS]: "male",
};

/**
 * Default delivery preference for document recipients.
 * Mirrors the backend DefaultDelivery enum in customer.model.ts.
 */
export enum DefaultDelivery {
  NO = "no",
  BUYER = "buyer",
  CUSTOMER = "customer",
}

/**
 * Subtype for VEHICLE_SIMULTANEOUS procedures.
 * Determines which operations are combined:
 * - N_N: Notification + Notification
 * - N_T: Notification + Transfer
 * - T_T: Transfer + Transfer
 */
export enum SimultaneousSubtype {
  N_N = "N_N",
  N_T = "N_T",
  T_T = "T_T",
}

/**
 * Ordered list of simultaneous subtypes for dropdown options.
 */
export const simultaneousSubtypeOptions: SimultaneousSubtype[] = [
  SimultaneousSubtype.N_N,
  SimultaneousSubtype.N_T,
  SimultaneousSubtype.T_T,
];

/**
 * Subtype for procedures with variants.
 * Each procedure type uses a subset of these values.
 */
export enum ProcedureSubtype {
  // Duplicate
  DUPLICATE_TECHNICAL_SHEET = "DUPLICATE_TECHNICAL_SHEET",
  DUPLICATE_REGISTRATION_CERT = "DUPLICATE_REGISTRATION_CERT",
  DUPLICATE_BOTH = "DUPLICATE_BOTH",

  // Deregistration
  DEREGISTRATION_TEMPORAL = "DEREGISTRATION_TEMPORAL",
  DEREGISTRATION_EXPORT = "DEREGISTRATION_EXPORT",

  // Reactivation
  REACTIVATION_REHABILITATION = "REACTIVATION_REHABILITATION",
  REACTIVATION_TEMPORAL = "REACTIVATION_TEMPORAL",
  REACTIVATION_EXPORT = "REACTIVATION_EXPORT",
}

export const DUPLICATE_SUBTYPES: ProcedureSubtype[] = [
  ProcedureSubtype.DUPLICATE_TECHNICAL_SHEET,
  ProcedureSubtype.DUPLICATE_REGISTRATION_CERT,
  ProcedureSubtype.DUPLICATE_BOTH,
];

export const DEREGISTRATION_SUBTYPES: ProcedureSubtype[] = [
  ProcedureSubtype.DEREGISTRATION_TEMPORAL,
  ProcedureSubtype.DEREGISTRATION_EXPORT,
];

export const REACTIVATION_SUBTYPES: ProcedureSubtype[] = [
  ProcedureSubtype.REACTIVATION_REHABILITATION,
  ProcedureSubtype.REACTIVATION_TEMPORAL,
  ProcedureSubtype.REACTIVATION_EXPORT,
];

/**
 * Procedure types where the primary non-customer party is the OWNER (Titular),
 * not the BUYER (Comprador). Mirrors `requiresParties: [OWNER]` in the backend
 * procedure-type registry.
 */
export const OWNER_ONLY_PROCEDURE_TYPES: readonly ProcedureTypes[] = [
  ProcedureTypes.VEHICLE_DUPLICATE,
  ProcedureTypes.VEHICLE_REACTIVATION,
  ProcedureTypes.VEHICLE_LEASING_RELEASE,
  ProcedureTypes.VEHICLE_RED_PLATE,
  ProcedureTypes.VEHICLE_GREEN_PLATE,
  ProcedureTypes.VEHICLE_REFORM_ANNOTATION,
  ProcedureTypes.VEHICLE_SERVICE_CHANGE,
  ProcedureTypes.VEHICLE_ADDRESS_CHANGE,
  ProcedureTypes.VEHICLE_ENV_BADGE,
  ProcedureTypes.VEHICLE_TRANSPORT_CARD,
  ProcedureTypes.PROPERTY_NOTE,
  ProcedureTypes.DOCUMENT_WRITING,
];

export const isOwnerOnlyProcedureType = (
  type: ProcedureTypes | null | undefined,
): boolean => !!type && OWNER_ONLY_PROCEDURE_TYPES.includes(type);

/**
 * Procedure types whose paperwork is physically shipped to a recipient and
 * therefore participate in delivery tracking. Mirrors the backend
 * `ProcedureTypeRegistry.supportsDeliveryTracking`.
 *
 * VEHICLE_SIMULTANEOUS appears in this list but is conditional on its subtype:
 * only `T_T` and `N_T` ship paperwork; pure `N_N` (notification + notification)
 * never does. Use `supportsDeliveryTracking` for the precise check.
 */
export const DELIVERY_TRACKING_PROCEDURE_TYPES: readonly ProcedureTypes[] = [
  ProcedureTypes.VEHICLE_REGISTRATION,
  ProcedureTypes.VEHICLE_TRANSFER,
  ProcedureTypes.VEHICLE_DUPLICATE,
  ProcedureTypes.VEHICLE_ENV_BADGE,
  ProcedureTypes.VEHICLE_SIMULTANEOUS,
  ProcedureTypes.VEHICLE_SERVICE_CHANGE,
  ProcedureTypes.VEHICLE_TRANSPORT_CARD,
  ProcedureTypes.VEHICLE_REACTIVATION,
];

const SIMULTANEOUS_DELIVERY_SUBTYPES: ReadonlySet<SimultaneousSubtype> =
  new Set([SimultaneousSubtype.T_T, SimultaneousSubtype.N_T]);

export const supportsDeliveryTracking = (
  type: ProcedureTypes | null | undefined,
  simultaneousSubtype?: SimultaneousSubtype | null,
): boolean => {
  if (!type || !DELIVERY_TRACKING_PROCEDURE_TYPES.includes(type)) return false;
  if (type !== ProcedureTypes.VEHICLE_SIMULTANEOUS) return true;
  return (
    !!simultaneousSubtype &&
    SIMULTANEOUS_DELIVERY_SUBTYPES.has(simultaneousSubtype)
  );
};

/**
 * Procedure types that have no document-collection step: the document upload
 * UI is hidden, they can be sent without uploads, and they are created
 * directly in PENDING instead of DRAFT.
 *
 * Mirrors the backend `ProcedureTypeRegistry.requiresDocuments` — keep the
 * two in sync. Used by the create form, where the procedure does not yet
 * exist and so `procedure.requiresDocuments` is not available.
 */
const TYPES_WITHOUT_DOCUMENTS: ReadonlySet<ProcedureTypes> = new Set([
  ProcedureTypes.VEHICLE_ENV_BADGE,
]);

export const requiresDocuments = (
  type: ProcedureTypes | null | undefined,
): boolean => !type || !TYPES_WITHOUT_DOCUMENTS.has(type);

/**
 * Procedure types whose workflow includes a DGT/Thempus step. For those,
 * `DELIVERED_TO_DGT` is written by the DGT-result saga, so offering it in the
 * manual status selector would fight the integration — the option is omitted
 * (P-2464). Every other type has no DGT step at all, so the manual transition
 * is the only way to record a state that is operationally real.
 *
 * Mirrors the backend `ProcedureTypeRegistry.supportsThempus` (the per-type
 * `supportsThempus` config flag) — keep the two in sync. The backend spec
 * `apps/api/libs/procedure/src/domain/model/procedure-type.registry.spec.ts`
 * pins this exact set and points back here if it drifts.
 */
export const THEMPUS_PROCEDURE_TYPES: readonly ProcedureTypes[] = [
  ProcedureTypes.VEHICLE_REGISTRATION,
  ProcedureTypes.VEHICLE_TRANSFER,
  ProcedureTypes.VEHICLE_NOTIFICATION,
  ProcedureTypes.VEHICLE_SIMULTANEOUS,
  ProcedureTypes.VEHICLE_DEREGISTRATION,
  ProcedureTypes.VEHICLE_TRANSFER_RECEIPT,
];

export const supportsThempus = (
  type: ProcedureTypes | null | undefined,
): boolean => !!type && THEMPUS_PROCEDURE_TYPES.includes(type);
